import { z } from 'zod/v3';

// ============================================================================
// 공통 Enum 스키마
// ============================================================================

/**
 * 알림 카테고리
 * @description 알림 목록 조회 시 필터링 카테고리
 * - ALL: 전체
 * - PRODUCT: 상품 혜택 캠페인
 * - EVENT: 이벤트 혜택 캠페인
 * - QUOTE: 견적 알림
 */
export const notificationCategorySchema = z.enum([
  'ALL',
  'PRODUCT',
  'EVENT',
  'QUOTE',
]);
export type NotificationCategory = z.infer<typeof notificationCategorySchema>;

/**
 * 견적 알림 표시 상태
 * @description 견적 요청의 현재 진행 상태
 * - SUBMITTED: 문의 작성 (배정된 파트너 없음)
 * - QUOTE_ARRIVED: 견적 전달 (파트너 배정됨, 견적서 미발송)
 * - ANSWER_ARRIVED: 답변 도착 (채팅 시작)
 */
export const quoteDisplayStatusSchema = z.enum([
  'SUBMITTED',
  'QUOTE_ARRIVED',
  'ANSWER_ARRIVED',
]);
export type QuoteDisplayStatus = z.infer<typeof quoteDisplayStatusSchema>;

/**
 * 견적 유형 (알림용)
 * @description 알림에서 사용되는 견적 유형
 * - COMPARE: 비교견적 (최대 10명 파트너 배정)
 * - ONE_TO_ONE: 1:1 견적 (지정 파트너에게 직접 요청)
 * - GRADE_BASED: 등급 기반 견적
 */
export const notificationQuoteTypeSchema = z.enum([
  'COMPARE',
  'ONE_TO_ONE',
  'GRADE_BASED',
]);
export type NotificationQuoteType = z.infer<typeof notificationQuoteTypeSchema>;

/**
 * 서비스 유형 (알림용)
 * @description 현재 TAX(세무) 고정
 * - TAX: 세무 서비스
 */
export const notificationServiceTypeSchema = z.enum(['TAX']);
export type NotificationServiceType = z.infer<
  typeof notificationServiceTypeSchema
>;

/**
 * 견적 상태 (알림용)
 * @description 견적 알림의 활성/비활성 상태
 * - ACTIVE: 활성 (7일 이내)
 * - INACTIVE: 비활성 (7일 초과)
 */
export const quoteStatusSchema = z.enum(['ACTIVE', 'INACTIVE']);

// ============================================================================
// 캠페인 알림 관련 스키마
// ============================================================================

/**
 * 캠페인 상품 아이템 스키마
 * @description 캠페인 알림(PRODUCT 타입)에 포함되는 상품 정보. EVENT 타입은 products가 빈 배열.
 * @note MarketSelectProductResponseV2 타입 기반 (알림에서 사용되는 필드만 정의)
 */
export const campaignProductItemSchema = z.object({
  /** 할인율 (%, 예: 24 → 24% 할인) */
  discountPercentage: z.number().optional(),
  /** 무료배송 여부 */
  isFreeShipping: z.boolean(),
  /** 품절 여부 */
  isOutOfStock: z.boolean(),
  /** 대표 이미지 URL 목록 */
  mainImages: z.array(z.string()).optional(),
  /** 상품 ID (OFFER) */
  offerId: z.number().optional(),
  /** 판매 가격 (원 단위, 할인 적용된 최종 가격) */
  price: z.number(),
  /** 할인 전 원가 (원 단위) */
  priceBeforeDiscount: z.number().optional(),
  /** 상품명 (예: "A4 복사용지 5000매") */
  productName: z.string(),
});
export type CampaignProductItem = z.infer<typeof campaignProductItemSchema>;

/**
 * 캠페인 알림 스키마
 * @description PRODUCT, EVENT 타입 알림에 포함되는 캠페인 정보
 * @example PRODUCT 타입
 * {
 *   "campaignId": 101,
 *   "campaignCode": "SPRING_2026",
 *   "catchphrase": "봄맞이 사무용품 특가",
 *   "thumbnail": "https://via.placeholder.com/400x200?text=Campaign1",
 *   "landingUrl": "/campaigns/101",
 *   "products": [{ ... }]
 * }
 * @example EVENT 타입 (products 빈 배열)
 * {
 *   "campaignId": 202,
 *   "campaignCode": "TAX_EVENT_2026",
 *   "catchphrase": "세무이지 오픈 기념 이벤트",
 *   "thumbnail": "https://via.placeholder.com/400x200?text=Event1",
 *   "landingUrl": "/events/202",
 *   "products": []
 * }
 */
export const campaignNotificationSchema = z.object({
  /** 캠페인 코드 (예: "SPRING_2026", "TAX_EVENT_2026") */
  campaignCode: z.string(),
  /** 캠페인 ID */
  campaignId: z.number(),
  /** 캐치프레이즈 - 캠페인 홍보 문구 (예: "봄맞이 사무용품 특가", "세무이지 오픈 기념 이벤트") */
  catchphrase: z.string().optional(),
  /** 랜딩 URL - 클릭 시 이동할 경로 (예: "/campaigns/101", "/events/202") */
  landingUrl: z.string(),
  /** 캠페인 상품 목록 - PRODUCT 타입은 상품 있음, EVENT 타입은 빈 배열 */
  products: z.array(campaignProductItemSchema),
  /** 썸네일 이미지 URL (400x200 권장) */
  thumbnail: z.string().optional(),
});
export type CampaignNotification = z.infer<typeof campaignNotificationSchema>;

// ============================================================================
// 견적 알림 관련 스키마
// ============================================================================

/**
 * 견적 알림 스키마
 * @description QUOTE 타입 알림에 포함되는 견적 정보
 * @example
 * {
 *   "quoteRequestId": 1,
 *   "quoteType": "COMPARE",
 *   "serviceType": "TAX",
 *   "requestDate": "2026-03-05",
 *   "displayStatus": "ANSWER_ARRIVED",
 *   "totalResponseCount": 5,
 *   "sentResponseCount": 2
 * }
 */
export const quoteNotificationSchema = z.object({
  /** 채팅방 오픈 횟수 */
  chatRoomOpenCount: z.number(),
  /**
   * 표시 상태 - 견적 요청의 현재 진행 상태
   * - SUBMITTED: 문의 작성 (배정된 파트너 없음)
   * - QUOTE_ARRIVED: 견적 도착 (파트너 배정됨, 견적서 미발송)
   * - ANSWER_ARRIVED: 답변 도착 (견적서 발송됨)
   */
  displayStatus: quoteDisplayStatusSchema,
  /** 견적 요청 ID - 상세 조회 시 사용 */
  quoteRequestId: z.number(),
  /** 견적 유형 (COMPARE: 비교견적, ONE_TO_ONE: 1:1 견적, GRADE_BASED: 등급 기반) */
  quoteType: notificationQuoteTypeSchema,
  /** 요청일시 (ISO 8601 형식) */
  requestAt: z.string(),
  /** 발송된 견적서 수 - 파트너가 실제로 견적서를 보낸 횟수 */
  sentResponseCount: z.number(),
  /** 서비스 유형 (현재 TAX 고정) */
  serviceType: notificationServiceTypeSchema,
  /** 견적 상태 (ACTIVE: 활성, INACTIVE: 비활성/만료) */
  status: quoteStatusSchema,
  /** 전체 응답 수 - 배정된 파트너 수 (비교견적은 최대 10명) */
  totalResponseCount: z.number(),
  /** 읽지 않은 채팅 수 */
  unreadChatCount: z.number(),
});
export type QuoteNotification = z.infer<typeof quoteNotificationSchema>;

// ============================================================================
// 알림 아이템 및 목록 스키마
// ============================================================================

/**
 * 알림 아이템 스키마
 * @description 알림 목록(B9)의 개별 아이템
 * @see GET /api/market-place/notifications
 *
 * type별 데이터 구조:
 * | type    | quote | campaign | 설명               |
 * |---------|-------|----------|-------------------|
 * | QUOTE   | 있음  | null     | 견적 알림          |
 * | PRODUCT | null  | 있음     | 상품 혜택 캠페인    |
 * | EVENT   | null  | 있음     | 이벤트 혜택 캠페인  |
 */
export const notificationItemSchema = z.object({
  /** 캠페인 알림 정보 - PRODUCT, EVENT 타입일 때만 존재 (QUOTE 타입은 null) */
  campaign: campaignNotificationSchema.optional(),
  /** 알림 생성일시 (ISO 8601 형식, 예: "2026-03-05T11:00:00") */
  createdAt: z.string(),
  /** 견적 알림 정보 - QUOTE 타입일 때만 존재 (PRODUCT, EVENT 타입은 null) */
  quote: quoteNotificationSchema.optional(),
  /** 알림 유형 (QUOTE: 견적, PRODUCT: 상품 혜택, EVENT: 이벤트 혜택) */
  type: z.enum(['QUOTE', 'PRODUCT', 'EVENT']),
});
export type NotificationItem = z.infer<typeof notificationItemSchema>;

/**
 * 알림 목록 응답 스키마
 * @description 알림 목록 조회(B9) 응답
 * @see GET /api/market-place/notifications
 * @param category - 필터링 카테고리 (ALL | QUOTE | PRODUCT | EVENT)
 * @note 03-11 변경: page, size 파라미터 제거됨. totalCount 필드 제거됨 (optional 처리)
 */
export const notificationListSchema = z.object({
  /** 알림 목록 - category 필터에 따라 필터링된 결과 */
  items: z.array(notificationItemSchema),
});
export type NotificationList = z.infer<typeof notificationListSchema>;
